import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(date: Date | { toDate: () => Date }): string {
  const d = 'toDate' in date ? date.toDate() : date;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function calculateAge(birthDate: Date | { toDate: () => Date }): number {
  const d = 'toDate' in birthDate ? birthDate.toDate() : birthDate;
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const monthDiff = today.getMonth() - d.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < d.getDate())) {
    age--;
  }

  return age;
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export function generateShareToken(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < 24; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

// ===========================================
// AGE CALCULATION UTILITIES (for child photos)
// ===========================================

export interface AgeAtPhoto {
  years: number;
  months: number;
}

/**
 * Calculate age at a specific date given a birth date
 * Returns years and months for more precision
 */
export function calculateAgeAtDate(
  birthDate: Date | { toDate: () => Date },
  targetDate: Date | { toDate: () => Date }
): AgeAtPhoto {
  const birth = 'toDate' in birthDate ? birthDate.toDate() : birthDate;
  const target = 'toDate' in targetDate ? targetDate.toDate() : targetDate;

  let years = target.getFullYear() - birth.getFullYear();
  let months = target.getMonth() - birth.getMonth();

  // Adjust for day of month
  if (target.getDate() < birth.getDate()) {
    months--;
  }

  // Handle negative months
  if (months < 0) {
    years--;
    months += 12;
  }

  // Ensure non-negative values
  if (years < 0) {
    years = 0;
    months = 0;
  }

  return { years, months };
}

/**
 * Format age for display
 * Examples: "2 years 3 months", "6 months", "1 year", "newborn"
 */
export function formatAgeAtPhoto(age: AgeAtPhoto): string {
  const { years, months } = age;

  if (years === 0 && months === 0) {
    return 'newborn';
  }

  const parts: string[] = [];

  if (years > 0) {
    parts.push(`${years} ${years === 1 ? 'year' : 'years'}`);
  }

  if (months > 0) {
    parts.push(`${months} ${months === 1 ? 'month' : 'months'}`);
  }

  return parts.join(' ');
}

/**
 * Format age in compact form for badges
 * Examples: "2y 3m", "6m", "1y", "0m"
 */
export function formatAgeCompact(age: AgeAtPhoto): string {
  const { years, months } = age;

  if (years === 0 && months === 0) {
    return '0m';
  }

  if (years === 0) {
    return `${months}m`;
  }

  if (months === 0) {
    return `${years}y`;
  }

  return `${years}y ${months}m`;
}
